import * as React from 'react';
import { useGetCurrentClient } from '@frontend/app/hooks';
import { PostInsights } from '@frontend/gql/social/graphql';
import { LoadSpinner } from '@components';
import { useLocation } from 'react-router-dom';
import { usePostsData } from '../Social/Analytics/hooks/usePostsData';
import { useFilterFormData } from '../Social/Analytics/components/filters-form/hooks/useFilterFormData';
import { DateType, QueryVariables } from '../Social/Analytics/types';
import { useAnalyticsParams } from '../Social/Analytics/hooks/useAnalyticsParams';
import PostInsightsCard from '../Social/Analytics/components/post-insights-card';
import { NewSocialPostAssignment } from './components/newSocialPostAssignment';
import { useApplication } from '../Shared/context/applicationContext';

const { useState, useEffect, useMemo } = React;

export const NewMemberSocialPostList: React.FunctionComponent = () => {
  const [postToAssign, setPostToAssign] = useState<PostInsights | null>(null);
  const { projects, groups, isFetchingProjects, isFetchingGroups } = useFilterFormData();

  const { memberId } = useApplication();
  const DEFAULT_VARIABLES: Omit<QueryVariables, 'clientId'> = {
    dateType: 'allTime' as DateType,
    postTypes: [],
    includeEstimates: true,
    networks: [],
    projectIds: [],
    groupIds: [],
    memberIds: [memberId],
    segments: [],
    postDateIntervalEnabled: false,
    lifetimeInsightsEnabled: false,
  };

  const { client } = useGetCurrentClient();
  const clientCreatedDate = client?.createdDate;
  const clientId = client?.id;
    const { queryVariables, initialized: isAnalyticsInitialized } = useAnalyticsParams({
    clientId,
    defaultVariables: DEFAULT_VARIABLES,
    clientCreatedDate,
  });

  // Social Post App: URL params not required; still wait for includeAds to resolve
  const isIncludeAdsResolved = queryVariables.includeAds !== undefined;
  const initialized = Boolean(clientId && memberId) && (isAnalyticsInitialized || isIncludeAdsResolved);
  const {
      data: postsData,
      fetchingPosts: fetching,
  } = usePostsData({
      clientId,
      tab: 'posts',
      queryVariables,
      projects,
      groups,
      initialized,
  });
  const location = useLocation();
  const searchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const postIdFromUrl = searchParams.get('postId');

  useEffect(() => {
    if (postIdFromUrl && postsData?.client?.socialAnalytics?.posts?.results) {
      const match = postsData.client.socialAnalytics.posts.results.find(
        (p) => p.entityId === postIdFromUrl || p.mentionId === postIdFromUrl
      );
      if (match) {
        setPostToAssign(match);
      }
    }
  }, [postIdFromUrl, postsData]);
  // Handler for post click
  const handlePostClick = (post: PostInsights) => {
    setPostToAssign(post);
  };

  const sortedPosts = useMemo(() => {
    const results = postsData?.client?.socialAnalytics?.posts?.results ?? [];
    return [...results].sort(
      (a, b) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime()
    );
  }, [postsData]);

  if (isFetchingProjects || isFetchingGroups || fetching) {
    return (
      <div className="flex justify-center items-center h-full w-full">
        <LoadSpinner centered />
      </div>
    );
  }
  if (!postsData?.client?.socialAnalytics?.posts?.results) {
    return null;
  }
  if (postToAssign) {
    return (
      <NewSocialPostAssignment
        projects={projects}
        post={postToAssign}
        groups={groups}
      />
    );
  }
  return (
    <div className="h-[calc(100vh-116px)] w-full">
      <div className="grid grid-cols-2 gap-4 p-4">
        {sortedPosts.map((post) => (
          <button
            key={post.entityId}
            onClick={() => handlePostClick(post)}
            className="w-full text-left cursor-pointer appearance-none bg-transparent border-0 p-0 m-0"
            aria-label={`View details for post ${post.entityId}`}
          >
            <PostInsightsCard
              post={post}
              isSelected={false}
              removeSelectionCheckbox
            />
          </button>
        ))}
      </div>
    </div>
  );
};
